import { Affix, Anchor, Box, Group, Loader, Menu, Text, TextInput } from '@mantine/core';
import { useMediaQuery, useToggle } from '@mantine/hooks';
import { ReactComponent as CloseSvg } from 'assets/img/close.svg';
import plus, { ReactComponent as IconPlus } from 'assets/img/plus.svg';
import classNames from 'classnames';
import { ActionBar } from 'components/ActionBar';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { MainTitle } from 'components/MainTitle';
import { ModalFinishOrder } from 'components/ModalFinishOrder';
import { OrdersDownloadModal } from 'components/OrdersDownloadModal';
import { OrdersListMobile } from 'components/OrdersListMobile';
import { OrdersUploadModal } from 'components/OrdersUploadModal';
import { SelectMenuFilter } from 'components/SelectMenuFilter';
import { SuccessErrorInfo } from 'components/SuccessErrorInfo';
import { Layout } from 'features/Layouts/Layouts';
import { OrderFilterParams } from 'hooks/useOrderFiltersNew';
import { FC, useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { Helmet } from 'react-helmet';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAuthorizationHeader } from 'redux/Api/employedsApi/employedsApi';
import {
  useGetOrdersQuery,
  useGetTabOrdersQuery,
  useSignMultipleOrdersMutation,
  useAcceptManySummaryMutation,
} from 'redux/Api/ordersApi/ordersApi';
import { resetOrderInfo } from 'redux/ordersDetails/slice';
import { Button } from 'ui/Button';
import { InputSearch } from 'ui/InputSearch';
import { enumerate } from 'utils/strActions';
import { echo } from '../../App';
import upload from '../../components/OrdersUploadModal/img/iconUpload.svg';
import iconVolume from './img/iconVolume.svg';
import { ActionRequiredAlert } from './ActionRequiredAlert';
import { useCanManage } from 'hooks/useCanManage';
import { ActionRequiredAlertMobile } from './ActionRequiredAlertMobile';
import check from './img/check.svg';
import { ReactComponent as IconChevronLeft } from './img/iconChewronLeft.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import done from './img/iconDone.svg';
import { FiltersTablet } from './FiltersTablet';
import download from './img/iconDownload.svg';
import PageDown from './img/PageDown.svg';
import { ReactComponent as IconKebab } from './img/iconKebab.svg';
import { ReactComponent as IconSearchBtn } from './img/iconSearchBtn.svg';
import { ReactComponent as IconUpload } from './img/iconUpload.svg';
import { ReactComponent as IconVolume } from './img/iconVolume.svg';
import { MobileMenu } from './MobileMenu';
import styles from './OrdersContent.module.scss';
import { ReportGenerationAlert } from './ReportGenerationAlert';
import { ActionRequiredAlertCheque } from './ActionRequiredAlertCheque';
import { ActionRequiredAlertChequeMobile } from './ActionRequiredAlertChequeMobile';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { Channel } from 'laravel-echo';
import { RootState, store } from 'redux/store';
import { handleOrderChanged } from './handleOrderChanged';
import { OrdersListTablet } from 'components/OrdersListTablet';
import { OrdersDischargeModal } from 'components/OrdersDischargeModal';
import ScrollButton from 'components/ScrollButton';
import { TitleRow } from 'components/TitleRow';
import { OrdersList } from 'components/OrdersList';
import useYandexMetrica from 'hooks/useYandexMetrica';
import { SelectedItem } from 'components/EmployedsFilters/EmployedsAdaptiveFilter';
import { OrdersModalVolume } from 'components/OrdersModalVolume';
import { AppTooltip } from 'ui/AppTooltip';

export type AppDispatch = typeof store.dispatch;

type PropsTooltipButton = {
  label: string;
  onClick: () => void;
  src: string;
};

interface QueryParams {
  page: number;
  tab: string;
  search: string;
  type_ids: SelectedItem[];
  payment_type_ids: SelectedItem[];
  customer_ids: SelectedItem[];
  worker_ids: SelectedItem[];
  preset_ids: SelectedItem[];
  statuses: SelectedItem[];
  amount_from?: number;
  amount_to?: number;
  numbers_range?: { start: string; end: string };
  date_start_range?: { start: string; end: string };
  date_end_range?: { start: string; end: string };
  create_date_range?: { start: string; end: string };
  date_paid_range?: { start: string; end: string };
  act?: {
    number?: string;
    statuses?: string[];
    create_date_range?: { start: string; end: string };
    amount_from?: number;
    amount_to?: number;
  };
  receipts?: {
    number?: string;
    statuses?: string[];
  };
  fns_receipts_seen_status?: 0 | 1;
  per_page?: number;
}

interface DataTabsType {
  count: number | null;
  idx: number | null;
  key: string | null;
  name: string | null;
}

interface Item {
  id: string;
}

const mapPaymentTypeIds = (items?: SelectedItem[]) => items?.map((item) => item.id);

export const OrdersContent: FC = () => {
  const role = localStorage.getItem('role');
  const canManage = useCanManage();
  const { data: profileData } = useGetProfileQuery(undefined, {
    skip: role === 'worksupport',
  });

  const [isSearchOpen, toggleSearchOpen] = useToggle();
  const searchRef = useRef<HTMLInputElement>(null);
  const [mobileSearchClicked, setMobileSearchClicked] = useState(false);
  const [active, setActive] = useState(localStorage.getItem('ordersFilter') || 'all');
  const [signMultipleOrders, { isLoading: loadingUpdateOrder }] =
    useSignMultipleOrdersMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const [isSelectedAll, setIsSelectAll] = useState(false);
  const { data: dataTabs, isLoading: isLoadingTabs } = useGetTabOrdersQuery();
  const dispatch = useDispatch();
  const [isSelectMobile, setIsSelectMobile] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [showReportErrorNotification, setShowReportErrorNotification] = useState(false);
  const [reportErrorMessage, setReportErrorMessage] = useState('Ошибка генерации отчёта');
  const [showDocumentDownloadSuccess, setShowDocumentDownloadSuccess] = useState(false);
  const [showDocumentDownloadError, setShowDocumentDownloadError] = useState(false);
  const [reportFileId, setReportFileId] = useState<string>('');
  const [reportStatus, setReportStatus] = useState<'loading' | 'ready'>('loading');
  const [countСheque, setCountСheque] = useState<number>(0);
  const [searchValue, setSearchValue] = useState('');
  const [resetTrigger, setResetTrigger] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showModalVolume, setShowModalVolume] = useState(false);
  const [show1CModal, setShow1CModal] = useState(false);
  const [show1CErrorNotification, setShow1CErrorNotification] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [documentsFileId, setDocumentsFileId] = useState<string>('');
  const [documentsStatus, setDocumentsStatus] = useState<'loading' | 'ready'>('loading');
  const [showFinishConfirmModal, setShowFinishConfirmModal] = useState(false);
  const [downloadReport, setDownloadReport] = useState(false);
  const { sendGoal } = useYandexMetrica();
  const userInfo = useSelector((state: RootState) => state.userInfoSlice);
  const [loadindModal, setLoadingModal] = useState(false);
  const [summaryCount, setSummaryCount] = useState<number | null>(null);
  const [summaryTotalAmount, setSummaryTotalAmount] = useState<number | null>(null);

  const isMobile = useMediaQuery(`(width < 768px)`);
  const isMediumScreen = useMediaQuery('(width >= 768px)');
  const isLessThan1595px = useMediaQuery('(width < 1595px)');
  const isLessThan1390px = useMediaQuery('(width < 1400px)');
  const [openNav, setOpenNav] = useState<boolean | undefined>(false);
  const [acceptManySummary] = useAcceptManySummaryMutation();

  const loadFiltersFromStorage = () => {
    try {
      const saved = localStorage.getItem('ordersFilters');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Ошибка при загрузке фильтров из localStorage:', error);
    }
    return {
      type_ids: [],
      payment_type_ids: [],
      worker_ids: [],
      preset_ids: [],
      statuses: [],
      customer_ids: [],
      amount_from: undefined,
      amount_to: undefined,
      numbers_range: undefined,
      date_start_range: undefined,
      date_end_range: undefined,
      create_date_range: undefined,
      date_paid_range: undefined,
      act: undefined,
      receipts: undefined,
      fns_receipts_seen_status: undefined,
    };
  };
  const savedFilters = loadFiltersFromStorage();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    page: 1,
    tab: active,
    search: '',
    ...savedFilters,
  });

  const { data, isLoading, isFetching } = useGetOrdersQuery({
    ...queryParams,
    type_ids: queryParams.type_ids?.map((item) => parseInt(item.id, 10)),
    payment_type_ids: mapPaymentTypeIds(queryParams.payment_type_ids),
    customer_ids: queryParams.customer_ids?.map((item) => parseInt(item.id, 10)),
    worker_ids: queryParams.worker_ids?.map((item) => parseInt(item.id, 10)),
    preset_ids: queryParams.preset_ids?.map((item) => parseInt(item.id, 10)),
    statuses: queryParams.statuses?.map((item) => item.id),
    receipts: queryParams.receipts
      ? {
          ...queryParams.receipts,
          number: queryParams.receipts.number
            ? String(queryParams.receipts.number)
            : undefined,
        }
      : undefined,
    ...(queryParams.fns_receipts_seen_status !== undefined && {
      fns_receipts_seen: queryParams.fns_receipts_seen_status,
    }),
    ...(queryParams.date_paid_range && {
      date_paid_range: queryParams.date_paid_range,
    }),
  });
  const currentDataRef = useRef(data);

  useEffect(() => {
    currentDataRef.current = data;
  }, [data]);

  const queryParamsRef = useRef(queryParams);

  useEffect(() => {
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  const saveFiltersToStorage = (filters: Partial<typeof queryParams>) => {
    const filtersToSave = {
      type_ids: filters.type_ids || [],
      payment_type_ids: filters.payment_type_ids || [],
      customer_ids: filters.customer_ids || [],
      worker_ids: filters.worker_ids || [],
      preset_ids: filters.preset_ids || [],
      statuses: filters.statuses || [],
      amount_from: filters.amount_from,
      amount_to: filters.amount_to,
      numbers_range: filters.numbers_range,
      date_start_range: filters.date_start_range,
      date_end_range: filters.date_end_range,
      create_date_range: filters.create_date_range,
      date_paid_range: filters.date_paid_range,
      act: filters.act,
      receipts: filters.receipts,
      fns_receipts_seen_status: filters.fns_receipts_seen_status,
    };
    localStorage.setItem('ordersFilters', JSON.stringify(filtersToSave));
  };

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/orders?page=1&per_page=50&statuses%5B0%5D=payment_success&receipts%5Bstatuses%5D%5B0%5D=cancelled&fns_receipts_seen_status=0`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setCountСheque(data.meta.total);
        } else {
          setCountСheque(0);
        }
      } catch {
        setCountСheque(0);
      }
    };

    fetchCount();
  }, []);

  useEffect(() => {
    const restoredFilters = loadFiltersFromStorage();
    if (restoredFilters.receipts?.statuses?.includes('cancelled')) {
      const newParams = {
        ...restoredFilters,
        ...(restoredFilters.fns_receipts_seen_status !== undefined && {
          fns_receipts_seen_status: restoredFilters.fns_receipts_seen_status,
        }),
        page: 1,
      };
      setQueryParams((prev) => ({
        ...prev,
        ...newParams,
      }));
    } else {
      setQueryParams((prev) => ({
        ...prev,
        ...restoredFilters,
        page: 1,
      }));
    }
  }, []);

  useEffect(() => {
    if (data?.data && isSelectedAll) {
      const ids = data.data.map((item) => item.id);
      setSelected(ids);
    }
  }, [data?.data, isSelectedAll]);

  useEffect(() => {
    dispatch(resetOrderInfo());
  }, []);

  const isActionRequiredCount =
    dataTabs?.items?.find((item: DataTabsType) => item.key === 'customer-action-required')
      ?.count || 0;

  const isApprovingVolumeCount =
    dataTabs?.items?.find((item: DataTabsType) => item.key === 'approving_volume')
      ?.count || 0;

  const customerId = profileData?.id;

  //слушатель на заказы
  useEffect(() => {
    if (!profileData?.id) return;
    let channel: Channel | null = null;

    const onOrderChanged = async () => {
      const convertedParams = {
        ...queryParamsRef.current,
        type_ids: queryParamsRef.current.type_ids?.map((item) => parseInt(item.id, 10)),
        customer_ids: queryParamsRef.current.customer_ids?.map((item) =>
          parseInt(item.id, 10),
        ),
        worker_ids: queryParamsRef.current.worker_ids?.map((item) =>
          parseInt(item.id, 10),
        ),
        preset_ids: queryParamsRef.current.preset_ids?.map((item) =>
          parseInt(item.id, 10),
        ),
        payment_type_ids: mapPaymentTypeIds(queryParamsRef.current.payment_type_ids),
        statuses: queryParamsRef.current.statuses?.map((item) => item.id),
      };
      await handleOrderChanged(convertedParams, dispatch);
    };
    try {
      channel = echo.private(`orders-list.${customerId}`);
      channel.listen('.order.changed', onOrderChanged);
    } catch (error) {
      console.error('Failed to setup WebSocket listener:', error);
    }

    return () => {
      if (channel) {
        try {
          echo.leave(`orders-list.${customerId}`);
        } catch (error) {
          console.error('Failed to cleanup WebSocket listener:', error);
        }
      }
    };
  }, [customerId]);

  //слушатель на документы
  useEffect(() => {
    if (!profileData?.id) return;

    const eventTargetsCurrentUser = (payload: Record<string, unknown>) => {
      const initiator = payload.user_id ?? payload.userId ?? payload.initiator_user_id;
      if (initiator == null) return true;
      if (userInfo?.id == null) return true;
      return Number(initiator) === Number(userInfo.id);
    };

    const channel = echo.private(`file-generation-channel.${customerId}`);

    const handleFileCompleted = (e: any) => {
      if (!eventTargetsCurrentUser(e)) return;
      setReportFileId((currentFileId) => {
        if (String(e.file_id) === String(currentFileId)) {
          setReportStatus('ready');
        }
        return currentFileId;
      });

      setDocumentsFileId((currentFileId) => {
        if (String(e.file_id) === String(currentFileId)) {
          setDocumentsStatus('ready');
        }
        return currentFileId;
      });
    };

    const handleFileCancelled = (e: any) => {
      if (!eventTargetsCurrentUser(e)) return;
      setReportFileId((currentFileId) => {
        if (String(e.file_id) === String(currentFileId)) {
          setReportStatus('loading');
          return '';
        }
        return currentFileId;
      });

      setDocumentsFileId((currentFileId) => {
        if (String(e.file_id) === String(currentFileId)) {
          setDocumentsStatus('loading');
          return '';
        }
        return currentFileId;
      });
    };

    const handleFileFailed = (e: any) => {
      if (!eventTargetsCurrentUser(e)) return;
      const errorMessage = e.message || 'Ошибка генерации файла';

      setReportFileId((currentFileId) => {
        if (String(e.file_id) === String(currentFileId)) {
          setReportStatus('loading');
          setShowReportErrorNotification(true);
          setReportErrorMessage(errorMessage);
          return '';
        }
        return currentFileId;
      });

      setDocumentsFileId((currentFileId) => {
        if (String(e.file_id) === String(currentFileId)) {
          setDocumentsStatus('loading');
          setShowReportErrorNotification(true);
          setReportErrorMessage(errorMessage);
          return '';
        }
        return currentFileId;
      });
    };

    channel
      .listen('.file.generation.completed', handleFileCompleted)
      .listen('.file.generation.cancelled', handleFileCancelled)
      .listen('.file.generation.failed', handleFileFailed);

    return () => {
      channel
        .stopListening('.file.generation.completed', handleFileCompleted)
        .stopListening('.file.generation.cancelled', handleFileCancelled)
        .stopListening('.file.generation.failed', handleFileFailed);
    };
  }, [customerId, userInfo?.id]);

  const currentFilters: OrderFilterParams = {
    ...(queryParams.type_ids?.length > 0 && { type_ids: queryParams.type_ids }),
    ...(queryParams.payment_type_ids?.length > 0 && {
      payment_type_ids: queryParams.payment_type_ids,
    }),
    ...(queryParams.customer_ids?.length > 0 && {
      customer_ids: queryParams.customer_ids,
    }),
    ...(queryParams.worker_ids?.length > 0 && { worker_ids: queryParams.worker_ids }),
    ...(queryParams.preset_ids?.length > 0 && { preset_ids: queryParams.preset_ids }),
    ...(queryParams.statuses?.length > 0 && { statuses: queryParams.statuses }),
    ...(queryParams.amount_from !== undefined && {
      amount_from: queryParams.amount_from,
    }),
    ...(queryParams.amount_to !== undefined && { amount_to: queryParams.amount_to }),
    ...(queryParams.numbers_range && { numbers_range: queryParams.numbers_range }),
    ...(queryParams.date_start_range && {
      date_start_range: queryParams.date_start_range,
    }),
    ...(queryParams.date_end_range && { date_end_range: queryParams.date_end_range }),
    ...(queryParams.create_date_range && {
      create_date_range: queryParams.create_date_range,
    }),
    ...(queryParams.date_paid_range && { date_paid_range: queryParams.date_paid_range }),
    ...(queryParams.act && { act: queryParams.act }),
    ...(queryParams.receipts && { receipts: queryParams.receipts }),
  };

  const hasNextPage = data?.meta
    ? data?.meta?.last_page !== data?.meta?.current_page
    : false;

  const [intersectionRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: () => setQueryParams((prev) => ({ ...prev, page: prev.page + 1 })),
    rootMargin: '0px 0px 160px 0px',
  });

  const openModal = () => {
    setShowUploadModal(true);
  };

  const openModalVolume = () => {
    setShowModalVolume(true);
  };

  const openModal1C = () => {
    setShow1CModal(true);
  };

  const openDocumentsModal = () => {
    setShowDocumentsModal(true);
  };

  const clearExistingAlerts = () => {
    localStorage.removeItem('reportGenerationAlert_report');
    localStorage.removeItem('reportGenerationAlert_documents');

    const cid = profileData?.id;
    const uid = userInfo?.id;
    if (cid != null && uid != null) {
      localStorage.removeItem(`reportGenerationAlert_report_${cid}_${uid}`);
      localStorage.removeItem(`reportGenerationAlert_documents_${cid}_${uid}`);
    }

    setReportFileId('');
    setReportStatus('loading');
    setDocumentsFileId('');
    setDocumentsStatus('loading');
  };

  const handleGenerateDocuments = (fileId: string) => {
    clearExistingAlerts();
    setDocumentsFileId(fileId);
    setDocumentsStatus('loading');
  };

  useEffect(() => {
    if (!showFinishConfirmModal) {
      setSummaryCount(null);
      setSummaryTotalAmount(null);
      setLoadingModal(false);
    }
  }, [showFinishConfirmModal]);

  const fetchSummaryData = async () => {
    setLoadingModal(true);
    try {
      const result = await acceptManySummary({
        ...savedFilters,
        statuses: savedFilters.statuses?.length
          ? savedFilters.statuses.map((item: SelectedItem) => item.id)
          : ['customer_action_required'],
        type_ids: savedFilters.type_ids?.map((item: SelectedItem) =>
          parseInt(item.id, 10),
        ),
        customer_ids: savedFilters.customer_ids?.map((item: SelectedItem) =>
          parseInt(item.id, 10),
        ),
        worker_ids: savedFilters.worker_ids?.map((item: SelectedItem) =>
          parseInt(item.id, 10),
        ),
        preset_ids: savedFilters.preset_ids?.map((item: SelectedItem) =>
          parseInt(item.id, 10),
        ),
        payment_type_ids: mapPaymentTypeIds(savedFilters.payment_type_ids),
      }).unwrap();
      setSummaryCount(Number(result.count) || null);
      setSummaryTotalAmount(Number(result.total_amount) || null);
    } catch (error) {
      console.error('Ошибка получения данных:', error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleSignAct = () => {
    if (isAll) {
      fetchSummaryData();
    }
    setShowFinishConfirmModal(true);
  };

  const handleConfirmFinish = async () => {
    sendGoal('CompleteOrderConfirm');
    setShowFinishConfirmModal(false);
    await signMultipleOrders({
      items: isSelectedAll || selected.length === data?.meta?.total ? [] : selected,
      ...savedFilters,
      statuses: savedFilters.statuses?.map((item: SelectedItem) => item.id),
      type_ids: savedFilters.type_ids?.map((item: SelectedItem) => parseInt(item.id, 10)),
      customer_ids: savedFilters.customer_ids?.map((item: SelectedItem) =>
        parseInt(item.id, 10),
      ),
      worker_ids: savedFilters.worker_ids?.map((item: SelectedItem) =>
        parseInt(item.id, 10),
      ),
      preset_ids: savedFilters.preset_ids?.map((item: SelectedItem) =>
        parseInt(item.id, 10),
      ),
      payment_type_ids: mapPaymentTypeIds(savedFilters.payment_type_ids),
    })
      .then((data: any) => {
        if (data?.error) {
          setShowErrorNotification(true);
        } else {
          setShowNotification(true);
        }

        setSelected([]);
        setIsSelectAll(false);
        setIsSelectMobile(false);
      })
      .catch(() => console.log('Ошибка'));
  };

  const handleDownloadReport = async () => {
    sendGoal('GenerateReport');
    setDownloadReport(true);
    clearExistingAlerts();
    try {
      const requestBody: any = {};

      const hasSelectedItems =
        selected.length > 0 && !(isSelectedAll || selected.length === data?.meta?.total);

      if (hasSelectedItems) {
        requestBody.items = selected;
      } else {
        requestBody.filters = {
          ...(queryParams.create_date_range && {
            create_date_range: queryParams.create_date_range,
          }),
          ...(queryParams.date_paid_range && {
            date_paid_range: queryParams.date_paid_range,
          }),
          ...(queryParams.date_start_range && {
            date_start_range: queryParams.date_start_range,
          }),
          ...(queryParams.date_end_range && {
            date_end_range: queryParams.date_end_range,
          }),
          ...(queryParams.statuses?.length > 0 && {
            statuses: queryParams.statuses.map((item) => item.id),
          }),
          ...(queryParams.amount_from !== undefined && {
            amount_from: queryParams.amount_from,
          }),
          ...(queryParams.amount_to !== undefined && {
            amount_to: queryParams.amount_to,
          }),
          ...(queryParams.numbers_range && { numbers_range: queryParams.numbers_range }),
          ...(queryParams.type_ids?.length > 0 && {
            type_ids: queryParams.type_ids.map((item) => parseInt(item.id, 10)),
          }),
          ...(queryParams.customer_ids?.length > 0 && {
            customer_ids: queryParams.customer_ids.map((item) => parseInt(item.id, 10)),
          }),
          ...(queryParams.worker_ids?.length > 0 && {
            worker_ids: queryParams.worker_ids.map((item) => parseInt(item.id, 10)),
          }),
          ...(queryParams.preset_ids?.length > 0 && {
            preset_ids: queryParams.preset_ids.map((item) => parseInt(item.id, 10)),
          }),
          ...(queryParams.payment_type_ids?.length > 0 && {
            payment_type_ids: mapPaymentTypeIds(queryParams.payment_type_ids),
          }),
          ...(queryParams.act && { act: queryParams.act }),
          ...(queryParams.receipts && { receipts: queryParams.receipts }),
        };
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/orders/docs/generate-report`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
          headers: {
            Authorization: getAuthorizationHeader(),
            'Content-Type': 'application/json',
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        setDownloadReport(false);
        setReportFileId(String(result.file_id));
        setReportStatus('loading');
      } else {
        console.error('Не удалось сгенерировать отчет');
        setDownloadReport(false);
        setShowReportErrorNotification(true);
      }

      setSelected([]);
      setIsSelectAll(false);
    } catch (error) {
      console.error('Ошибка при генерации отчета:', error);
      setShowReportErrorNotification(true);
    }
  };

  const selectAllBtnDisabled = selected.length === data?.meta?.total || isSelectedAll;
  const showFullPageSkeleton = isLoading || (isFetching && queryParams.page === 1);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: searchValue,
      }));
    }
  };

  const mobileTabs =
    dataTabs?.items?.map((tab) => ({
      value: tab.key,
      label: tab.name,
    })) || [];

  const handleSelectAll = () => {
    const ids = data?.data?.map((item: Item) => item.id) ?? [];

    if (data?.meta?.total === ids.length) {
      setSelected([] as string[]);
      setIsSelectAll(false);
    } else {
      setIsSelectAll(true);
    }
    setSelected(ids);
  };

  const selectedCount = isSelectedAll ? data?.meta?.total ?? 0 : selected.length;

  const ordersToCheck =
    isSelectedAll || selected.length === 0
      ? data?.data ?? []
      : data?.data?.filter((order) => selected.includes(order.id)) ?? [];

  const finishedOrders = ordersToCheck.filter(
    (order) => order.status?.value === 'worker_finished_order',
  );

  const selectedCountFinish = finishedOrders.length;

  const totalAmount = finishedOrders.reduce((sum, order) => sum + order.amount, 0);

  const activeTab = localStorage.getItem('ordersFilter') || active;

  const activeTabName =
    mobileTabs.find((tab) => tab.value === activeTab)?.label || 'Активные';

  const handleTabChange = (newTab: string) => {
    localStorage.setItem('ordersFilter', newTab || 'all');
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      tab: newTab,
    }));
    setActive(newTab);
    setSelected([]);
    setIsSelectAll(false);
  };

  const handleMobileSearch = () => {
    if (searchValue === '') {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: '',
      }));
    } else {
      setQueryParams((prev) => ({
        ...prev,
        search: searchValue,
      }));
    }
    setMobileSearchClicked(true);
  };

  const handleMobileSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setMobileSearchClicked(false);

    if (e.target.value === '') {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: '',
      }));
    }
  };

  const handleTabClick = (val: string) => {
    handleTabChange(val);
  };

  const handleApplyFilters = (newFilters: OrderFilterParams) => {
    setQueryParams((prev) => {
      const updatedParams = {
        ...prev,
        page: 1,
        type_ids: [],
        payment_type_ids: [],
        customer_ids: [],
        worker_ids: [],
        preset_ids: [],
        statuses: [],
        amount_from: undefined,
        amount_to: undefined,
        numbers_range: undefined,
        date_start_range: undefined,
        date_end_range: undefined,
        create_date_range: undefined,
        date_paid_range: undefined,
        act: undefined,
        receipts: undefined,
        fns_receipts_seen_status: undefined,
        ...(newFilters.type_ids && { type_ids: newFilters.type_ids }),
        ...(newFilters.payment_type_ids?.length && {
          payment_type_ids: newFilters.payment_type_ids,
        }),
        ...(newFilters.customer_ids && { customer_ids: newFilters.customer_ids }),
        ...(newFilters.worker_ids && { worker_ids: newFilters.worker_ids }),
        ...(newFilters.preset_ids && { preset_ids: newFilters.preset_ids }),
        ...(newFilters.statuses && { statuses: newFilters.statuses }),
        ...(newFilters.amount_from !== undefined && {
          amount_from: newFilters.amount_from,
        }),
        ...(newFilters.amount_to !== undefined && { amount_to: newFilters.amount_to }),
        ...(newFilters.numbers_range && { numbers_range: newFilters.numbers_range }),
        ...(newFilters.date_start_range && {
          date_start_range: newFilters.date_start_range,
        }),
        ...(newFilters.date_end_range && { date_end_range: newFilters.date_end_range }),
        ...(newFilters.create_date_range && {
          create_date_range: newFilters.create_date_range,
        }),
        ...(newFilters.date_paid_range && {
          date_paid_range: newFilters.date_paid_range,
        }),
        ...(newFilters.act && { act: newFilters.act }),
        ...(newFilters.receipts && { receipts: newFilters.receipts }),
      };

      if (updatedParams.receipts?.statuses?.includes('cancelled')) {
        const savedFilters = loadFiltersFromStorage();

        const fnsReceiptsSeenStatus =
          savedFilters.fns_receipts_seen_status !== undefined
            ? savedFilters.fns_receipts_seen_status
            : prev.fns_receipts_seen_status !== undefined
            ? prev.fns_receipts_seen_status
            : undefined;

        if (fnsReceiptsSeenStatus !== undefined) {
          updatedParams.fns_receipts_seen_status = fnsReceiptsSeenStatus;
        }
      }

      saveFiltersToStorage(updatedParams);

      return updatedParams;
    });

    setSelected([]);
    setIsSelectAll(false);
  };

  const handleResetFilters = () => {
    if (isLoading || isFetching) return;
    const resetParams = {
      page: 1,
      type_ids: [],
      payment_type_ids: [],
      customer_ids: [],
      worker_ids: [],
      preset_ids: [],
      statuses: [],
      amount_from: undefined,
      amount_to: undefined,
      numbers_range: undefined,
      date_start_range: undefined,
      date_end_range: undefined,
      create_date_range: undefined,
      date_paid_range: undefined,
      act: undefined,
      receipts: undefined,
      fns_receipts_seen_status: undefined,
    };

    setQueryParams((prev) => ({
      ...prev,
      ...resetParams,
    }));

    saveFiltersToStorage(resetParams);

    setResetTrigger((prev) => prev + 1);
    setSelected([]);
    setIsSelectAll(false);
  };

  const withCheckbox = true;

  const dataTitle =
    role === 'director'
      ? ['№', 'Дата заказа', 'Услуга', 'Исполнитель', 'Сумма, ₽', 'Статус', 'Дата оплаты']
      : [
          'Период',
          'Заказчик',
          'Исполнитель',
          'Услуга',
          'Стоимость',
          'Вознаграждение оператора',
          'Статус',
        ];

  const titleClassName =
    role === 'director'
      ? withCheckbox
        ? 'ordersCustomerCheckbox'
        : 'ordersCustomer'
      : withCheckbox
      ? 'ordersWorksupportCheckbox'
      : 'orders';

  const currentPageIds = data?.data?.map((item) => item.id) ?? [];
  const status =
    currentPageIds.length === 0 || (!isSelectedAll && selected.length === 0)
      ? 'none'
      : isSelectedAll || selected.length === currentPageIds.length
      ? 'all'
      : 'some';

  const handleToggle = () => {
    if (status === 'all') {
      setSelected([]);
      setIsSelectAll(false);
      return;
    }

    setSelected(currentPageIds);
    setIsSelectAll(false);
  };

  const isAll =
    isSelectedAll ||
    selected.length === (data?.meta?.total ?? 0) ||
    (!isSelectedAll && selected.length === 0);

  return (
    <Layout setOpenNav={setOpenNav}>
      <Helmet>
        <title>Заказы</title>
      </Helmet>

      {isMobile ? (
        <>
          {isActionRequiredCount > 0 ? (
            <ActionRequiredAlertMobile
              tab={active}
              title='Заказы требующие действия'
              subtitle={`Требуется действия у ${isActionRequiredCount} ${enumerate(
                isActionRequiredCount,
                ['заказа', 'заказов', 'заказов'],
              )}`}
              onClick={() => {
                setQueryParams((prev) => ({
                  ...prev,
                  page: 1,
                  tab: 'customer-action-required',
                }));
                setActive('customer-action-required');
                localStorage.setItem('ordersFilter', 'customer-action-required');
                setSelected([]);
                setIsSelectAll(false);
              }}
            />
          ) : null}
          {isApprovingVolumeCount > 0 ? (
            <ActionRequiredAlertMobile
              tab={active}
              title={'Подтверждение объема'}
              subtitle={`Необходимо подтвердить объем выполненных работ у ${isApprovingVolumeCount} ${enumerate(
                isApprovingVolumeCount,
                ['заказа', 'заказов', 'заказов'],
              )}`}
              onClick={() => {
                setQueryParams((prev) => ({
                  ...prev,
                  page: 1,
                  tab: 'approving_volume',
                }));
                setActive('approving_volume');
                localStorage.setItem('ordersFilter', 'approving_volume');
                setSelected([]);
                setIsSelectAll(false);
              }}
            />
          ) : null}
        </>
      ) : null}
      {isMobile ? (
        <Box className={styles.root} pos={'relative'}>
          <div className={styles.stickyHeader} style={{ padding: 0 }}>
            {isSearchOpen ? (
              <TextInput
                flex={1}
                mb='10px'
                placeholder='Искать...'
                type='search'
                autoFocus
                ref={searchRef}
                radius='8px'
                value={searchValue}
                styles={{ input: { height: '48px', border: 'none' } }}
                leftSection={
                  <IconChevronLeft
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    onClick={() => {
                      if (isFetching) return;
                      setSearchValue('');
                      setMobileSearchClicked(false);
                      setQueryParams((prev) => ({
                        ...prev,
                        search: '',
                      }));
                      toggleSearchOpen();
                    }}
                  />
                }
                rightSection={
                  <Group gap='8px' mr='6'>
                    {searchValue !== '' && (
                      <IconClose
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        onClick={() => {
                          if (isFetching) return;
                          setSearchValue('');
                          setMobileSearchClicked(false);
                          setQueryParams((prev) => ({
                            ...prev,
                            search: '',
                          }));
                          searchRef?.current?.focus();
                        }}
                      />
                    )}
                    {!mobileSearchClicked && (
                      <Box
                        style={{
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        onClick={handleMobileSearch}>
                        <IconSearchBtn
                          style={{ width: '71px', height: '40px', cursor: 'pointer' }}
                        />
                      </Box>
                    )}
                  </Group>
                }
                rightSectionWidth={
                  searchValue !== '' && !mobileSearchClicked
                    ? 110
                    : mobileSearchClicked
                    ? 50
                    : 86
                }
                onChange={handleMobileSearchChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setQueryParams((prev) => ({
                      ...prev,
                      page: 1,
                      search: searchValue,
                    }));
                    setMobileSearchClicked(true);
                  }
                }}
              />
            ) : (
              <Group justify='space-between' align='center'>
                <MainTitle title='Заказы' count={data?.meta?.total || 0} />

                {active === 'customer-action-required' && data?.meta?.total !== 0 ? (
                  isSelectMobile ? (
                    <Anchor
                      component='button'
                      onClick={() => {
                        setIsSelectMobile(false);
                        setSelected([]);
                        setIsSelectAll(false);
                      }}
                      c='#002CFB'
                      fz='14'>
                      Отменить
                    </Anchor>
                  ) : (
                    <Anchor
                      component='button'
                      display={isLoading ? 'none' : 'initial'}
                      onClick={() => setIsSelectMobile(true)}
                      c='#002CFB'
                      fz='14'>
                      Выбрать
                    </Anchor>
                  )
                ) : null}
              </Group>
            )}

            {isSearchOpen ? null : isLoadingTabs ? (
              <ContentLoader
                speed={2}
                height={34}
                backgroundColor={'#FFFFFF'}
                foregroundColor={'#D8E4FB8C'}
                style={{ borderRadius: '8px', marginBottom: '12px' }}>
                <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
              </ContentLoader>
            ) : (
              <MobileMenu
                mobileTabs={mobileTabs}
                handleTabClick={handleTabClick}
                activeTabName={activeTabName}
                toggleSearchOpen={toggleSearchOpen}
                onApplyFilters={handleApplyFilters}
                onResetFilters={handleResetFilters}
                initialFilters={{
                  ...currentFilters,
                  payment_type_ids: queryParams.payment_type_ids ?? [],
                }}
                resetTrigger={resetTrigger}
                setActive={handleTabChange}
                active={localStorage.getItem('ordersFilter') || active}
              />
            )}

            {countСheque > 0 ? (
              <ActionRequiredAlertChequeMobile
                count={countСheque}
                onClick={() => {
                  setQueryParams((prev) => ({
                    ...prev,
                    page: 1,
                    statuses: [{ id: 'payment_success', name: 'Оплачен' }],
                    receipts: { statuses: ['cancelled'] },
                    fns_receipts_seen_status: 0,
                  }));
                  saveFiltersToStorage({
                    statuses: [{ id: 'payment_success', name: 'Оплачен' }],
                    receipts: { statuses: ['cancelled'] },
                    fns_receipts_seen_status: 0,
                  });
                }}
              />
            ) : null}
          </div>
          {showFullPageSkeleton ? (
            <LoadingSkeleton />
          ) : (
            <OrdersListMobile
              data={data?.data}
              intersectionComponent={
                hasNextPage && isMobile ? <div ref={intersectionRef} /> : null
              }
              withCheckbox={isSelectMobile && active === 'customer-action-required'}
              selected={selected}
              setSelected={(val) => {
                if (selected.length !== data?.meta?.total) {
                  setIsSelectAll(false);
                }
                setSelected(val);
              }}
              key={data?.meta?.total}
            />
          )}
        </Box>
      ) : null}

      {isMediumScreen ? (
        <Box className={styles.root} mt={-20}>
          <ReportGenerationAlert
            key={`${reportFileId || documentsFileId}_${
              reportFileId ? 'report' : 'documents'
            }`}
            fileId={reportFileId || documentsFileId}
            status={reportFileId ? reportStatus : documentsStatus}
            userId={userInfo?.id}
            customerId={profileData?.id}
            onClose={() => {
              setReportFileId('');
              setReportStatus('loading');
              setDocumentsFileId('');
              setDocumentsStatus('loading');
            }}
            onCancel={() => {
              setReportFileId('');
              setReportStatus('loading');
              setDocumentsFileId('');
              setDocumentsStatus('loading');
            }}
            onDownloadSuccess={() => {
              setShowDocumentDownloadSuccess(true);
            }}
            onDownloadError={() => {
              setShowDocumentDownloadError(true);
            }}
            onFileFailed={(message) => {
              setReportErrorMessage(message || 'Ошибка генерации отчёта');
              setShowReportErrorNotification(true);
            }}
          />

          <div className={styles.stickyHeader}>
            <div>
              <div className={styles.root_title}>
                <MainTitle title='Заказы' count={data?.meta?.total || 0} />
                <div className={styles.root_title_btn}>
                  <>
                    {(!openNav && isLessThan1595px) || isLessThan1390px ? (
                      role !== 'director' ? (
                        <div className={styles.root_title_btnWrapper}>
                          {active === 'finished' ? (
                            <>
                              <TooltipButton
                                label={`Документы`}
                                onClick={openDocumentsModal}
                                src={download}
                              />
                              <TooltipButton
                                label={`Выгрузка 1С`}
                                onClick={openModal1C}
                                src={download}
                              />
                              <Button
                                onClick={handleDownloadReport}
                                className={styles.headerBtn}
                                theme='outline_icon'>
                                <img
                                  src={PageDown}
                                  alt='Отчет'
                                  style={{ width: '20px', height: '20px' }}
                                />
                              </Button>

                              {canManage && (
                                <Button
                                  theme='outline'
                                  icon
                                  src={check}
                                  onClick={handleSignAct}
                                  loading={loadingUpdateOrder}>
                                  Завершить
                                </Button>
                              )}
                            </>
                          ) : null}
                          <Link to='/beneficiar/create'>
                            <Button icon src={plus} theme='filed'>
                              Заказчик
                            </Button>
                          </Link>
                        </div>
                      ) : (
                        <div className={styles.root_title_btnWrapper}>
                          <TooltipButton
                            label={`Документы`}
                            onClick={openDocumentsModal}
                            src={download}
                          />
                          <TooltipButton
                            label={`Выгрузка 1С`}
                            onClick={openModal1C}
                            src={download}
                          />
                          <TooltipButton
                            label={`Отчет`}
                            onClick={handleDownloadReport}
                            src={PageDown}
                          />

                          {canManage && (
                            <TooltipButton
                              label={`Объем`}
                              onClick={openModalVolume}
                              src={iconVolume}
                            />
                          )}
                          {canManage && (
                            <TooltipButton
                              label={`Массовая загрузка`}
                              onClick={openModal}
                              src={upload}
                            />
                          )}
                          {canManage && (
                            <Button
                              theme='outline'
                              icon
                              src={check}
                              onClick={handleSignAct}
                              loading={loadingUpdateOrder}>
                              Завершить
                            </Button>
                          )}

                          {canManage && (
                            <Link to='/orders/create'>
                              <Button
                                onClick={() => dispatch(resetOrderInfo())}
                                icon
                                src={plus}
                                theme='filed'>
                                Создать заказ
                              </Button>
                            </Link>
                          )}
                        </div>
                      )
                    ) : role !== 'director' ? (
                      <div className={styles.root_title_btnWrapper}>
                        {active === 'finished' ? (
                          <>
                            <Button
                              theme='outline'
                              icon
                              src={download}
                              onClick={openModal1C}>
                              Выгрузка 1С
                            </Button>
                            <Button
                              theme='outline'
                              icon
                              src={download}
                              onClick={openDocumentsModal}>
                              Документы
                            </Button>
                            <Button
                              theme='outline'
                              icon
                              src={download}
                              onClick={handleDownloadReport}>
                              Отчет
                            </Button>
                            {canManage && (
                              <Button
                                theme='outline'
                                icon
                                src={check}
                                onClick={handleSignAct}
                                loading={loadingUpdateOrder}>
                                Завершить
                              </Button>
                            )}
                          </>
                        ) : null}
                        <Link to='/beneficiar/create'>
                          <Button icon src={plus} theme='filed'>
                            Заказчик
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className={styles.root_title_btnWrapper}>
                        <Button theme='outline' icon src={download} onClick={openModal1C}>
                          Выгрузка 1С
                        </Button>
                        <Button
                          theme='outline'
                          icon
                          src={download}
                          onClick={openDocumentsModal}>
                          Документы
                        </Button>
                        <Button
                          theme='outline'
                          icon
                          src={download}
                          loading={downloadReport}
                          onClick={handleDownloadReport}>
                          Отчет
                        </Button>

                        {canManage && (
                          <>
                            <Button
                              theme='outline'
                              icon
                              src={iconVolume}
                              onClick={() => {
                                openModalVolume();
                              }}>
                              Объем
                            </Button>

                            <Button
                              theme='outline'
                              icon
                              src={check}
                              onClick={handleSignAct}
                              loading={loadingUpdateOrder}>
                              Завершить
                            </Button>

                            <Button
                              theme='outline'
                              icon
                              src={upload}
                              onClick={() => {
                                openModal();
                              }}>
                              Массовая загрузка
                            </Button>

                            <Link to='/orders/create'>
                              <Button
                                onClick={() => dispatch(resetOrderInfo())}
                                icon
                                src={plus}
                                theme='filed'>
                                Создать заказ
                              </Button>
                            </Link>
                          </>
                        )}
                      </div>
                    )}
                  </>
                </div>
              </div>

              {isActionRequiredCount > 0 ? (
                <ActionRequiredAlert
                  tab={active}
                  onClick={() => {
                    setQueryParams((prev) => ({
                      ...prev,
                      page: 1,
                      tab: 'customer-action-required',
                    }));
                    setActive('customer-action-required');
                    localStorage.setItem('ordersFilter', 'customer-action-required');
                    setSelected([]);
                    setIsSelectAll(false);
                  }}
                  title={'Заказы требующие действия'}
                  subtitle={`Требуется действия у ${isActionRequiredCount} ${enumerate(
                    isActionRequiredCount,
                    ['заказа', 'заказов', 'заказов'],
                  )}`}
                />
              ) : null}

              {isApprovingVolumeCount > 0 ? (
                <ActionRequiredAlert
                  tab={active}
                  onClick={() => {
                    setQueryParams((prev) => ({
                      ...prev,
                      page: 1,
                      tab: 'approving_volume',
                    }));
                    setActive('approving_volume');
                    localStorage.setItem('ordersFilter', 'approving_volume');
                    setSelected([]);
                    setIsSelectAll(false);
                  }}
                  title={'Подтверждение объема'}
                  subtitle={`Необходимо подтвердить объем выполненных работ у ${isApprovingVolumeCount} ${enumerate(
                    isApprovingVolumeCount,
                    ['заказа', 'заказов', 'заказов'],
                  )}`}
                />
              ) : null}

              {countСheque > 0 ? (
                <ActionRequiredAlertCheque
                  count={countСheque}
                  onClick={() => {
                    setQueryParams((prev) => ({
                      ...prev,
                      page: 1,
                      statuses: [{ id: 'payment_success', name: 'Оплачен' }],
                      receipts: { statuses: ['cancelled'] },
                      fns_receipts_seen_status: 0,
                    }));
                    saveFiltersToStorage({
                      statuses: [{ id: 'payment_success', name: 'Оплачен' }],
                      receipts: { statuses: ['cancelled'] },
                      fns_receipts_seen_status: 0,
                    });
                  }}
                />
              ) : null}

              <div className={styles.root_header}>
                <div className={styles.root_headerItem}>
                  <InputSearch
                    notFocus
                    style={{
                      minWidth: '230px',
                      maxWidth: '260px',
                      height: '48px',
                    }}
                    value={searchValue}
                    onKeyDown={handleKeyDown}
                    onSearch={() => {
                      setQueryParams((prev) => ({
                        ...prev,
                        page: 1,
                        search: searchValue,
                      }));
                    }}
                    fetching={isFetching}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchValue(e.target.value);

                      if (e.target.value === '') {
                        setQueryParams((prev) => ({
                          ...prev,
                          page: 1,
                          search: '',
                        }));
                      }
                    }}
                  />
                  {isLoadingTabs ? (
                    <ContentLoader
                      speed={2}
                      height={'44px'}
                      width='100%'
                      className={styles.tab_loader}
                      backgroundColor={'#FFFFFF'}
                      foregroundColor={'#D8E4FB8C'}>
                      <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                    </ContentLoader>
                  ) : (
                    <div className={styles.menu}>
                      <SelectMenuFilter
                        active={localStorage.getItem('ordersFilter') || active}
                        lists={dataTabs?.items}
                        setActive={handleTabChange}
                      />
                    </div>
                  )}
                </div>

                <FiltersTablet
                  onApplyFilters={handleApplyFilters}
                  onResetFilters={handleResetFilters}
                  initialFilters={currentFilters}
                  resetTrigger={resetTrigger}
                />
              </div>

              {!((!openNav && isLessThan1595px) || isLessThan1390px) && (
                <div style={{ backgroundColor: '#fff', borderRadius: '8px 8px 0 0' }}>
                  <TitleRow
                    data={dataTitle}
                    title={titleClassName}
                    withCheckbox={withCheckbox}
                    selected={status}
                    toggleSelect={handleToggle}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            {showFullPageSkeleton ? (
              <LoadingSkeleton fullWidth={true} />
            ) : (
              <>
                {(!openNav && isLessThan1595px) || isLessThan1390px ? (
                  <OrdersListTablet
                    data={data?.data}
                    withCheckbox={true}
                    selected={selected}
                    setSelected={(val) => {
                      if (selected.length !== data?.meta?.total) {
                        setIsSelectAll(false);
                      }
                      setSelected(val);
                    }}
                    intersectionComponent={
                      hasNextPage ? <div ref={intersectionRef} /> : null
                    }
                    key={data?.meta?.total}
                  />
                ) : (
                  <OrdersList
                    data={data?.data || []}
                    withCheckbox={true}
                    selected={selected}
                    openNav={openNav}
                    hideTitleRow
                    setSelected={(val) => {
                      if (selected.length !== data?.meta?.total) {
                        setIsSelectAll(false);
                      }
                      setSelected(val);
                    }}
                    intersectionComponent={
                      hasNextPage && !isMobile ? <div ref={intersectionRef} /> : null
                    }
                    key={data?.meta?.total}
                  />
                )}
              </>
            )}
          </div>
          <Affix position={{ bottom: 50, left: openNav ? 80 : 286 }} zIndex={1000}>
            <ScrollButton />
          </Affix>
        </Box>
      ) : null}

      {!isLoading && isFetching && queryParams.page > 1 && data?.data?.length > 0 && (
        <div
          className={styles.loaders}
          style={{ paddingBottom: isMobile ? '150px' : '25px' }}>
          <Loader size='xs' color='#002cfb' />
        </div>
      )}
      {showUploadModal && (
        <OrdersUploadModal show={showUploadModal} setShow={setShowUploadModal} />
      )}

      {showModalVolume && (
        <OrdersModalVolume show={showUploadModal} setShow={setShowModalVolume} />
      )}

      {show1CModal && (
        <OrdersDischargeModal
          show={show1CModal}
          setShow={setShow1CModal}
          onGenerateDocuments={handleGenerateDocuments}
          requestBody={(() => {
            const body: any = {};

            const hasSelectedItems =
              selected.length > 0 &&
              !(isSelectedAll || selected.length === data?.meta?.total);

            if (hasSelectedItems) {
              body.items = selected;
            } else {
              body.filters = {
                ...(queryParams.create_date_range && {
                  create_date_range: queryParams.create_date_range,
                }),
                ...(queryParams.date_paid_range && {
                  date_paid_range: queryParams.date_paid_range,
                }),
                ...(queryParams.date_start_range && {
                  date_start_range: queryParams.date_start_range,
                }),
                ...(queryParams.date_end_range && {
                  date_end_range: queryParams.date_end_range,
                }),
                ...(queryParams.statuses?.length > 0 && {
                  statuses: queryParams.statuses.map((item) => item.id),
                }),
                ...(queryParams.amount_from !== undefined && {
                  amount_from: queryParams.amount_from,
                }),
                ...(queryParams.amount_to !== undefined && {
                  amount_to: queryParams.amount_to,
                }),
                ...(queryParams.numbers_range && {
                  numbers_range: queryParams.numbers_range,
                }),
                ...(queryParams.type_ids?.length > 0 && {
                  type_ids: queryParams.type_ids.map((item) => item.id),
                }),
                ...(queryParams.payment_type_ids?.length > 0 && {
                  payment_type_ids: mapPaymentTypeIds(queryParams.payment_type_ids),
                }),
                ...(queryParams.customer_ids?.length > 0 && {
                  customer_ids: queryParams.customer_ids.map((item) => item.id),
                }),
                ...(queryParams.worker_ids?.length > 0 && {
                  worker_ids: queryParams.worker_ids.map((item) => item.id),
                }),
                ...(queryParams.preset_ids?.length > 0 && {
                  preset_ids: queryParams.preset_ids.map((item) => item.id),
                }),
                ...(queryParams.act && { act: queryParams.act }),
                ...(queryParams.receipts && { receipts: queryParams.receipts }),
              };
            }

            return body;
          })()}
          onError={() => {
            setShow1CErrorNotification(true);
          }}
        />
      )}
      {showDownloadModal && (
        <OrdersDownloadModal show={showDownloadModal} setShow={setShowDownloadModal} />
      )}
      {showDocumentsModal && (
        <OrdersDownloadModal
          show={showDocumentsModal}
          setShow={setShowDocumentsModal}
          selectedOrders={selected}
          onGenerateDocuments={handleGenerateDocuments}
          filters={
            selected.length === 0
              ? {
                  ...(queryParams.create_date_range && {
                    create_date_range: queryParams.create_date_range,
                  }),
                  ...(queryParams.date_paid_range && {
                    date_paid_range: queryParams.date_paid_range,
                  }),
                  ...(queryParams.date_start_range && {
                    date_start_range: queryParams.date_start_range,
                  }),
                  ...(queryParams.date_end_range && {
                    date_end_range: queryParams.date_end_range,
                  }),
                  ...(queryParams.statuses?.length > 0 && {
                    statuses: queryParams.statuses.map((item) => item.id),
                  }),
                  ...(queryParams.amount_from !== undefined && {
                    amount_from: queryParams.amount_from,
                  }),
                  ...(queryParams.amount_to !== undefined && {
                    amount_to: queryParams.amount_to,
                  }),
                  ...(queryParams.numbers_range && {
                    numbers_range: queryParams.numbers_range,
                  }),
                  ...(queryParams.type_ids?.length > 0 && {
                    type_ids: queryParams.type_ids.map((item) => item.id),
                  }),
                  ...(queryParams.payment_type_ids?.length > 0 && {
                    payment_type_ids: mapPaymentTypeIds(queryParams.payment_type_ids),
                  }),
                  ...(queryParams.customer_ids?.length > 0 && {
                    customer_ids: queryParams.customer_ids.map((item) => item.id),
                  }),
                  ...(queryParams.worker_ids?.length > 0 && {
                    worker_ids: queryParams.worker_ids.map((item) => item.id),
                  }),
                  ...(queryParams.preset_ids?.length > 0 && {
                    preset_ids: queryParams.preset_ids.map((item) => item.id),
                  }),
                  ...(queryParams.act && { act: queryParams.act }),
                  ...(queryParams.receipts && { receipts: queryParams.receipts }),
                }
              : undefined
          }
        />
      )}

      {showNotification && (
        <SuccessErrorInfo
          type='success'
          text='Заявка на завершение принята'
          handleClose={() => setShowNotification(false)}
        />
      )}
      {showErrorNotification && (
        <SuccessErrorInfo
          type='error'
          text='Ошибка принятия заявки'
          handleClose={() => setShowErrorNotification(false)}
        />
      )}
      {showReportErrorNotification && (
        <SuccessErrorInfo
          type='error'
          text={reportErrorMessage}
          handleClose={() => setShowReportErrorNotification(false)}
        />
      )}
      {showDocumentDownloadSuccess && (
        <SuccessErrorInfo
          type='success'
          text='Документы успешно скачаны'
          handleClose={() => setShowDocumentDownloadSuccess(false)}
        />
      )}
      {showDocumentDownloadError && (
        <SuccessErrorInfo
          type='error'
          text='Ошибка при скачивании'
          handleClose={() => setShowDocumentDownloadError(false)}
        />
      )}
      {show1CErrorNotification && (
        <SuccessErrorInfo
          type='error'
          text='Ошибка при скачивании'
          handleClose={() => setShow1CErrorNotification(false)}
        />
      )}
      {showFinishConfirmModal && (
        <ModalFinishOrder
          isShowFinishModal={showFinishConfirmModal}
          setIsShowFinishModal={setShowFinishConfirmModal}
          onConfirm={handleConfirmFinish}
          isLoading={loadingUpdateOrder}
          isAll={isAll}
          totalAmount={summaryTotalAmount || totalAmount}
          selectedCount={summaryCount || selectedCountFinish}
          loadindModal={loadindModal}
        />
      )}
      {selected.length ? (
        <ActionBar setOpen={() => setSelected([])}>
          <div className={styles.actionBar}>
            <p>
              {enumerate(selectedCount, ['Выбран', 'Выбрано', 'Выбрано'])} {selectedCount}{' '}
              {enumerate(selectedCount, ['заказ', 'заказа', 'заказов'])}
            </p>
            <Button
              className={classNames(
                styles.actionBar_btn,
                selectAllBtnDisabled && styles.actionBar_btn_disabled,
              )}
              theme='filed'
              onClick={handleSelectAll}
              disabled={selectAllBtnDisabled}>
              Выбрать все
            </Button>
            <CloseSvg
              onClick={() => {
                setSelected([]);
                setIsSelectAll(false);
              }}
            />
          </div>
        </ActionBar>
      ) : null}
      {isSelectMobile ? (
        <Affix
          hiddenFrom='sm'
          position={{ bottom: 0, right: 0, left: 0 }}
          p='12px 16px 34px 16px'
          style={{ backgroundColor: '#E6EAFF', borderRadius: '8px 8px 0px 0px' }}>
          <Text fw='600' fz='18px' lh={'24px'} c='#121922'>
            {enumerate(selectedCount, ['Выбран', 'Выбрано', 'Выбрано'])} {selectedCount}{' '}
            {enumerate(selectedCount, ['заказ', 'заказа', 'заказов'])}
          </Text>
          <Group mt='12px' grow>
            <Button
              onClick={handleSelectAll}
              theme='outline'
              disabled={selectAllBtnDisabled}
              className={classNames(
                styles.actionBar_btn,
                selectAllBtnDisabled && styles.actionBar_btn_disabled,
              )}>
              Выбрать все
            </Button>
            {active === 'customer-action-required' ? (
              <Button
                icon
                theme='filed'
                src={done}
                loading={loadingUpdateOrder}
                className={classNames(
                  styles.actionBar_btn,
                  !selected.length && styles.actionBar_btn_disabled,
                )}
                onClick={() => selected.length && handleSignAct()}>
                Завершить
              </Button>
            ) : active === 'finished' ? (
              <>
                <Button
                  icon
                  theme='filed'
                  src={done}
                  loading={loadingUpdateOrder}
                  className={classNames(
                    styles.actionBar_btn,
                    !selected.length && styles.actionBar_btn_disabled,
                  )}
                  onClick={() => selected.length && handleSignAct()}>
                  Завершить
                </Button>
              </>
            ) : (
              <Button
                icon
                theme='filed'
                src={done}
                loading={loadingUpdateOrder}
                className={classNames(
                  styles.actionBar_btn,
                  !selected.length && styles.actionBar_btn_disabled,
                )}
                onClick={() => selected.length && handleSignAct()}>
                Завершить
              </Button>
            )}
          </Group>
        </Affix>
      ) : canManage ? (
        <Affix
          hiddenFrom='sm'
          position={{ bottom: 34, right: 0, left: 0 }}
          w='fit-content'
          m='0 auto'
          style={{ display: 'flex', justifyContent: 'center' }}>
          <Group gap='12px' wrap='nowrap'>
            <Menu position='top-start' width={210}>
              <Menu.Target>
                <Box
                  p='12px'
                  bg='#002CFB'
                  h='48px'
                  w='48px'
                  // onClick={toggleSearchOpen}
                  style={{ borderRadius: '4px', cursor: 'pointer' }}>
                  <IconKebab />
                </Box>
              </Menu.Target>

              <Menu.Dropdown
                p='4px 12px'
                style={{
                  border: 'none',
                  boxShadow: '0px 2px 12px 0px rgba(0, 32, 51, 0.10)',
                }}>
                <Menu.Item p='0' onClick={openModal}>
                  <Group gap='8px' align='center' h='46px'>
                    <IconUpload />
                    <Text span fz='16px' fw='500' c='#002CFB' lh='22px'>
                      Массовая загрузка
                    </Text>
                  </Group>
                </Menu.Item>
                <Menu.Item p='0' onClick={openModalVolume}>
                  <Group gap='8px' align='center' h='46px'>
                    <IconVolume />
                    <Text span fz='16px' fw='500' c='#002CFB' lh='22px'>
                      Объем
                    </Text>
                  </Group>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            <Link to='/orders/create'>
              <Box
                p='12px'
                bg='#002CFB'
                h='48px'
                w='144px'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  color: 'white',
                  borderRadius: '4px',
                }}>
                Создать <IconPlus />
              </Box>
            </Link>
            <ScrollButton isMobile={isMobile} />
          </Group>
        </Affix>
      ) : (
        <Affix
          hiddenFrom='sm'
          position={{ bottom: 34, right: 34 }}
          w='fit-content'
          m='0 auto'
          style={{ display: 'flex', justifyContent: 'center' }}>
          <ScrollButton isMobile={isMobile} />
        </Affix>
      )}
    </Layout>
  );
};

export const TooltipButton = ({ label, onClick, src }: PropsTooltipButton) => {
  return (
    <AppTooltip label={label}>
      <div>
        <Button onClick={onClick} className={styles.headerBtn} theme='outline_icon'>
          <img src={src} alt={label} style={{ width: '20px', height: '20px' }} />
        </Button>
      </div>
    </AppTooltip>
  );
};
